import { createSlice } from "@reduxjs/toolkit";
import { readURL } from "../../urlState";

// Default: hoy en zona horaria de Chile (donde corre el cron Fargate con
// TZ=America/Santiago y donde están particionados los geojson en S3).
// No usar toISOString() — devuelve UTC y entre las ~20:00 y ~04:00 Chile
// adelanta el día y el front pide datos del día siguiente que aún no existen.
const todayISO = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const STORAGE_REGION_KEY = "clbb-waze:selectedRegion";

const url = readURL();

const loadInitialRegion = () => {
  // Sólo deep-link explícito por URL preselecciona región. Si no, arranca en
  // BLANCO: el mapa parte mostrando todo Chile y el usuario elige región/comuna
  // o usa su ubicación (los combos quedan vacíos).
  if (url.region) return url.region;
  return null;
};

// Modelo temporal: SIEMPRE rango. Single-day = dateStart === dateEnd.
// timeStart/timeEnd = null,null → día completo. Ambos seteados → filtro fino.
const loadInitialDateStart = () => url.dateStart || url.date || todayISO();
// dateEnd default = dateStart (single-day), NO la fecha de hoy — sino al
// recargar una URL sin dateEnd un día posterior queda un rango fantasma.
const loadInitialDateEnd   = () => url.dateEnd || url.dateStart || url.date || todayISO();
const loadInitialTime = (key, legacyHour) => {
  if (url[key]) return /^\d{2}:\d{2}$/.test(url[key]) ? url[key] : null;
  // Backward compat: ?hour=HH (sin minuto) — sólo valida para timeStart, no end.
  if (legacyHour && url.hour && url.hour !== "day" && /^\d{2}$/.test(url.hour)) {
    const mm = url.minute && /^\d{2}$/.test(url.minute) ? url.minute : "00";
    return `${url.hour}:${mm}`;
  }
  return null;
};

export const dataSlice = createSlice({
  name: "dataState",
  initialState: {
    regions: [],

    selectedRegion: loadInitialRegion(),

    // Rango principal (período A). dateStart/dateEnd siempre seteados;
    // timeStart/timeEnd null = día completo.
    dateStart: loadInitialDateStart(),
    dateEnd:   loadInitialDateEnd(),
    timeStart: loadInitialTime("timeStart", true),
    timeEnd:   loadInitialTime("timeEnd", false),

    selectedCommune: url.commune || null,
    geolocationAttempted: Boolean(url.region),

    showJams: url.jams !== "0",
    showAlerts: url.alerts !== "0",

    catalog: null,
    geojson: null,
    alertsGeojson: null,
    communesIndex: null,

    // Período B (comparativa). Mismo shape que el A.
    compareEnabled: false,
    compareDateStart: null,
    compareDateEnd: null,
    compareTimeStart: null,
    compareTimeEnd: null,
    compareCatalog: null,
    compareGeojson: null,
    compareAlertsGeojson: null,
    compareMode: "overlay",

    selectedFeature: null,
    timelinePlaying: false,

    catalogLoading: false,
    geojsonLoading: false,
    error: null,
  },
  reducers: {
    setRegions: (state, action) => { state.regions = action.payload; },
    setSelectedRegion: (state, action) => {
      state.selectedRegion = action.payload;
      try { localStorage.setItem(STORAGE_REGION_KEY, action.payload); } catch { /* ignore */ }
      state.catalog = null;
      state.geojson = null;
      state.selectedCommune = null;
    },
    setSelectedCommune: (state, action) => { state.selectedCommune = action.payload; },
    markGeolocationAttempted: (state) => { state.geolocationAttempted = true; },

    // Rango principal: setters atómicos (no acoplar para que UI pueda
    // editar un extremo sin tocar el otro).
    setDateRange: (state, action) => {
      // payload: { start, end }
      state.dateStart = action.payload.start;
      state.dateEnd   = action.payload.end || action.payload.start;
      state.catalog = null;
      state.geojson = null;
    },
    setTimeRange: (state, action) => {
      // payload: { start, end } — strings "HH:MM" o null,null
      state.timeStart = action.payload.start || null;
      state.timeEnd   = action.payload.end || null;
      // Filtro client-side: no invalida geojson ni catalog.
    },

    setCatalog: (state, action) => { state.catalog = action.payload; },
    setCommunesIndex: (state, action) => { state.communesIndex = action.payload; },
    setGeojson: (state, action) => { state.geojson = action.payload; },
    setAlertsGeojson: (state, action) => { state.alertsGeojson = action.payload; },
    setCatalogLoading: (state, action) => { state.catalogLoading = action.payload; },
    setGeojsonLoading: (state, action) => { state.geojsonLoading = action.payload; },
    setError: (state, action) => { state.error = action.payload; },
    setShowJams: (state, action) => { state.showJams = action.payload; },
    setShowAlerts: (state, action) => { state.showAlerts = action.payload; },
    setSelectedFeature: (state, action) => { state.selectedFeature = action.payload; },
    setTimelinePlaying: (state, action) => { state.timelinePlaying = action.payload; },

    setCompareEnabled: (state, action) => {
      state.compareEnabled = action.payload;
      if (!action.payload) {
        state.compareDateStart = null;
        state.compareDateEnd = null;
        state.compareTimeStart = null;
        state.compareTimeEnd = null;
        state.compareCatalog = null;
        state.compareGeojson = null;
        state.compareAlertsGeojson = null;
      } else if (!state.compareDateStart) {
        // Al activar la comparativa por primera vez, defaultear B a 1 semana
        // atrás (misma hora) para arrancar con algo razonable.
        const d = new Date(`${state.dateStart}T12:00:00`);
        d.setDate(d.getDate() - 7);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        state.compareDateStart = iso;
        state.compareDateEnd = iso;
        state.compareTimeStart = state.timeStart;
        state.compareTimeEnd = state.timeEnd;
      }
    },
    setCompareDateRange: (state, action) => {
      state.compareDateStart = action.payload.start;
      state.compareDateEnd   = action.payload.end || action.payload.start;
      state.compareCatalog = null;
      state.compareGeojson = null;
      state.compareAlertsGeojson = null;
    },
    setCompareTimeRange: (state, action) => {
      state.compareTimeStart = action.payload.start || null;
      state.compareTimeEnd   = action.payload.end || null;
    },
    setCompareCatalog: (state, action) => { state.compareCatalog = action.payload; },
    setCompareGeojson: (state, action) => { state.compareGeojson = action.payload; },
    setCompareAlertsGeojson: (state, action) => { state.compareAlertsGeojson = action.payload; },
    setCompareMode: (state, action) => { state.compareMode = action.payload; },
  },
});

export const {
  setRegions,
  setSelectedRegion,
  setDateRange,
  setTimeRange,
  setCatalog,
  setCommunesIndex,
  setGeojson,
  setAlertsGeojson,
  setCatalogLoading,
  setGeojsonLoading,
  setError,
  setSelectedCommune,
  setShowJams,
  setShowAlerts,
  setSelectedFeature,
  setTimelinePlaying,
  setCompareEnabled,
  setCompareDateRange,
  setCompareTimeRange,
  setCompareCatalog,
  setCompareGeojson,
  setCompareAlertsGeojson,
  setCompareMode,
  markGeolocationAttempted,
} = dataSlice.actions;

export default dataSlice.reducer;
